'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import { AlertCircle, FileText, PlusIcon, X } from 'lucide-react'
import Image from 'next/image'
import React, { useCallback, useMemo, useRef } from 'react'

import { DocxIcon, MicrosoftExcel, PdfIcon, PptIcon } from '@/components/icons'
import { cn } from '@/lib/cn'
import type { FileType } from '@/types/fileType'
import { MIMETYPE_MAPPER } from '@/utils/mimeTypeToExtension'
import { Button } from '../button'
import { useToast } from '../toast'

type AttachmentContextProps = {
  files: FileType[]
  mimeType?: string[]
  maxFile?: number
  onChangeFiles: (files: FileType[]) => void
  handleFiles: (fileList: FileList) => void
}

const AttachmentContext = React.createContext({} as AttachmentContextProps)

const attachmentVariants = cva(
  'flex w-full items-center gap-2 rounded-md border px-3 py-2',
  {
    variants: {
      isError: {
        true: 'border-system-state-error',
        false: 'border-system-border-primary',
      },
    },
    defaultVariants: {
      isError: false,
    },
  },
)

type AttachmentProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof attachmentVariants> & {
    files: FileType[]
    onChangeFiles: (files: FileType[]) => void
    maxFile?: number
    maxSizeInMB?: number
    mimeType?: string[]
  }

const Attachment = React.forwardRef<HTMLDivElement, AttachmentProps>(
  (
    {
      className,
      isError,
      files,
      onChangeFiles,
      maxFile = 3,
      maxSizeInMB = 3,
      mimeType,
      ...props
    },
    ref,
  ) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const { toast } = useToast()

    const handleFiles = useCallback(
      (fileList: FileList) => {
        const validFiles: FileType[] = []
        const invalidFiles: FileType[] = []
        const maxSize = maxSizeInMB * 1024 * 1024 // Converter MB para bytes

        const filesRemaining = maxFile - files.length

        if (filesRemaining <= 0) {
          toast({
            title: 'Limite de arquivos atingido',
            description: 'Você atingiu o limite de arquivos permitidos',
            variant: 'destructive',
            icon: AlertCircle,
          })
          return
        }

        const filesToProcess = Array.from(fileList).slice(0, filesRemaining)

        for (let i = 0; i < filesToProcess.length; i++) {
          const uploadedFile = filesToProcess[i]
          if (uploadedFile.size > maxSize) {
            invalidFiles.push({ file: uploadedFile })
            continue
          }

          if (!mimeType) {
            validFiles.push({ file: uploadedFile })
            continue
          }

          const uploadedMimeType = uploadedFile.type
          const isValidMimeType = mimeType?.some((type) => {
            if (type.endsWith('/*')) {
              const [baseType] = type.split('/')
              return uploadedMimeType.startsWith(baseType)
            }
            return uploadedMimeType === type
          })

          if (isValidMimeType) {
            validFiles.push({ file: uploadedFile })
          } else {
            invalidFiles.push({ file: uploadedFile })
          }
        }

        onChangeFiles([...files, ...validFiles])

        if (invalidFiles.length) {
          const errorMessage = `Arquivos inválidos\n${invalidFiles
            .map(({ file }) => file?.name)
            .join('\n')}`
          toast({
            title: 'Arquivos inválidos',
            description: errorMessage,
            variant: 'destructive',
            icon: AlertCircle,
          })
        }
      },
      [maxSizeInMB, files, maxFile, onChangeFiles, toast, mimeType],
    )

    const handleDragOver = useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
      },
      [],
    )

    const handleDrop = useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        const { files } = event.dataTransfer
        handleFiles(files)
      },
      [handleFiles],
    )

    const handleButtonClick = useCallback(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click()
      }
    }, [])

    const providerProps = useMemo(
      () => ({
        files,
        mimeType,
        maxFile,
        onChangeFiles,
        handleFiles,
      }),
      [files, maxFile, mimeType, onChangeFiles, handleFiles],
    )
    return (
      <AttachmentContext.Provider value={providerProps}>
        <div
          ref={ref}
          className={cn(attachmentVariants({ className, isError }))}
          onClick={handleButtonClick}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          {...props}
        />
      </AttachmentContext.Provider>
    )
  },
)
Attachment.displayName = 'Attachment'

type AttachmentFileProps = React.HTMLAttributes<HTMLDivElement> & {
  fileType: FileType
}
const AttachmentFile = React.forwardRef<HTMLDivElement, AttachmentFileProps>(
  ({ className, fileType, ...props }, ref) => {
    const { file } = fileType
    const { files, onChangeFiles } = useAttachment()

    const handlePreview = useCallback((file: FileType) => {
      if ('preview' in file && file.preview) {
        return file.preview
      }

      if (file.file) {
        return URL.createObjectURL(file.file)
      }

      return null
    }, [])

    const handleRemove = useCallback(
      (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation()
        const index = files.findIndex(
          (f) =>
            (f.file?.name === file?.name && file?.name) ||
            (f.preview === fileType.preview && fileType.preview),
        )
        if (index !== -1) {
          onChangeFiles(files.filter((_, i) => i !== index))
        }
      },
      [file, fileType, files, onChangeFiles],
    )

    const isImage = useMemo(() => {
      const mimeType = fileType?.fileType || file?.type
      return mimeType?.startsWith('image/') ?? false
    }, [fileType, file])

    const imgPreview = useMemo(() => {
      if (!isImage) return null
      return handlePreview(fileType)
    }, [fileType, handlePreview, isImage])

    const fileName = useMemo(() => {
      return file?.name || fileType.preview?.split('/').pop() || 'Arquivo'
    }, [file, fileType])

    const fileIcon = useMemo(() => {
      const mimeType = fileType?.fileType || file?.type
      if (!mimeType) return null

      const iconType = MIMETYPE_MAPPER[
        mimeType as keyof typeof MIMETYPE_MAPPER
      ] || [mimeType]

      const extension = iconType[0]

      switch (extension) {
        case 'doc':
        case 'docx':
          return (
            <div className="flex size-12">
              <DocxIcon />
            </div>
          )
        case 'xls':
        case 'xlsx':
        case 'csv':
          return (
            <div className="flex size-12">
              <MicrosoftExcel color="#000" className="size-12" />
            </div>
          )
        case 'ppt':
        case 'pptx':
          return (
            <div className="flex size-12">
              <PptIcon />
            </div>
          )
        case 'pdf':
          return (
            <div className="flex size-12">
              <PdfIcon />
            </div>
          )
        default:
          return <FileText className="size-12 text-system-text-secondary" />
      }
    }, [fileType, file])

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center px-4 bg-system-background-base-contrast border border-system-border-secondary rounded-lg relative size-24',
          className,
        )}
        role="listitem"
        aria-label={fileName}
        {...props}
      >
        {imgPreview && isImage ? (
          <div className="relative size-8 sm:size-12 md:size-16 lg:size-20">
            <Image
              src={imgPreview}
              alt={`Preview ${fileName}`}
              fill
              sizes="100%"
              className="object-contain"
            />
          </div>
        ) : (
          <div className="flex size-full items-center justify-center">
            {fileIcon}
          </div>
        )}

        <Button
          className="absolute right-0 top-0 cursor-pointer"
          size="icon"
          variant="ghost"
          type="button"
          onClick={handleRemove}
          aria-label={`Remove ${fileName}`}
        >
          <X className="size-6 text-system-text-primary" />
        </Button>
      </div>
    )
  },
)
AttachmentFile.displayName = 'AttachmentFile'

const AttachmentNew = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const { maxFile = 3, mimeType, handleFiles } = useAttachment()

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target

      if (files) {
        handleFiles(files)
      }
    },
    [handleFiles],
  )

  const handleButtonClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }, [])

  return (
    <div
      ref={ref}
      className={cn(
        'flex justify-center items-center cursor-pointer border system-border-primary border-dashed rounded-md size-24',
        className,
      )}
      onClick={handleButtonClick}
      role="button"
      aria-label="add-file"
      {...props}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={mimeType?.join(', ')}
        onChange={handleFileInputChange}
        className="hidden"
        multiple={maxFile > 1}
      />
      <PlusIcon className="size-6 text-system-primary-purple" />
    </div>
  )
})
AttachmentNew.displayName = 'AttachmentNew'

const useAttachment = () => {
  const context = React.useContext(AttachmentContext)

  if (!context) {
    throw new Error('useAttachment must be used within a <AttachmentProvider>')
  }

  return context
}

export { Attachment, AttachmentFile, AttachmentNew, useAttachment }
export type { AttachmentProps }
